import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faUndo, faExclamationTriangle, faPlug } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../shared/components/ui/LoadingSpinner'
import { SensorConfigFormData, SENSOR_PORTS } from '../types/sensor'
import { api } from '../services'

interface SensorConfigurationProps {
  selectedDevice: any
  selectedSensorNumber: number
  onSensorSelect: (deviceId: number, sensorNumber: number) => void
}

export function SensorConfiguration({ selectedDevice, selectedSensorNumber }: SensorConfigurationProps) {
  const [formData, setFormData] = useState<SensorConfigFormData>({
    sensorName: '',
    conversionScale: 1.0,
    conversionOffset: 0.0,
    sensorPosition: undefined,
    warnHigh: undefined,
    warnLow: undefined,
    warnChange: undefined,
    alertsEnabled: false,
  })
  const [hasChanges, setHasChanges] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [configData, setConfigData] = useState<any>(null)
  const [thresholdsData, setThresholdsData] = useState<any>(null)

  // Fetch sensor configuration when device or sensor changes
  useEffect(() => {
    if (!selectedDevice || !selectedSensorNumber) {
      setFormData({
        sensorName: '',
        conversionScale: 1.0,
        conversionOffset: 0.0,
        sensorPosition: undefined,
        warnHigh: undefined,
        warnLow: undefined,
        warnChange: undefined,
        alertsEnabled: false,
      })
      return
    }

    const fetchData = async () => {
      setIsLoading(true)
      try {
        // Fetch sensor config
        const configResponse = await api.getSensorConfig(selectedDevice.sigfoxId)
        setConfigData(configResponse.data)

        // Fetch sensor thresholds
        try {
          const thresholdsResponse = await api.getSensorThresholds(
            selectedDevice.deviceId || parseInt(selectedDevice.sigfoxId, 16),
            selectedSensorNumber
          )
          setThresholdsData(thresholdsResponse.data)
        } catch (err) {
          // Thresholds might not exist yet
          setThresholdsData(null)
        }
      } catch (error) {
        console.error('Failed to fetch sensor configuration:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [selectedDevice, selectedSensorNumber])

  // Initialize form data when sensor data loads
  useEffect(() => {
    if (configData && selectedSensorNumber) {
      // Find config for this sensor number
      const sensorKey = SENSOR_PORTS[selectedSensorNumber as keyof typeof SENSOR_PORTS]
      const config = configData?.[sensorKey]

      setFormData({
        sensorName: config?.sensorName || `Sensor ${selectedSensorNumber}`,
        conversionScale: config?.conversionScale || 1.0,
        conversionOffset: config?.conversionOffset || 0.0,
        sensorPosition: config?.sensorPosition || undefined,
        warnHigh: thresholdsData?.warnHigh || undefined,
        warnLow: thresholdsData?.warnLow || undefined,
        warnChange: thresholdsData?.warnChange || undefined,
        alertsEnabled: !!(thresholdsData?.warnHigh || thresholdsData?.warnLow),
      })
      setHasChanges(false)
    }
  }, [configData, thresholdsData, selectedSensorNumber])

  const handleInputChange = (field: keyof SensorConfigFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    if (!selectedDevice || !selectedSensorNumber) return

    setIsSaving(true)
    try {
      // Update thresholds if alerts are enabled
      if (formData.alertsEnabled) {
        await api.setSensorThresholds(
          selectedDevice.deviceId || parseInt(selectedDevice.sigfoxId, 16),
          selectedSensorNumber,
          {
            warnHigh: formData.warnHigh,
            warnLow: formData.warnLow,
            warnChange: formData.warnChange,
          }
        )
      }

      // Reset the form state
      setHasChanges(false)
      toast.success('Sensor configuration saved successfully!', { duration: 15000 })
    } catch (error) {
      console.error('Failed to save sensor configuration:', error)
      toast.error('Failed to save sensor configuration. Please try again.', { duration: 30000 })
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    if (configData && selectedSensorNumber) {
      const sensorKey = SENSOR_PORTS[selectedSensorNumber as keyof typeof SENSOR_PORTS]
      const config = configData?.[sensorKey]

      setFormData({
        sensorName: config?.sensorName || `Sensor ${selectedSensorNumber}`,
        conversionScale: config?.conversionScale || 1.0,
        conversionOffset: config?.conversionOffset || 0.0,
        sensorPosition: config?.sensorPosition || undefined,
        warnHigh: thresholdsData?.warnHigh || undefined,
        warnLow: thresholdsData?.warnLow || undefined,
        warnChange: thresholdsData?.warnChange || undefined,
        alertsEnabled: !!(thresholdsData?.warnHigh || thresholdsData?.warnLow),
      })
    }
    setHasChanges(false)
  }

  if (!selectedDevice || selectedSensorNumber === 0) {
    return (
      <div className="p-6 text-center">
        <FontAwesomeIcon icon={faPlug} className="h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Sensor</h3>
        <p className="text-gray-600">Choose a device and sensor from the Sensor List tab to configure its settings.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const portName = SENSOR_PORTS[selectedSensorNumber as keyof typeof SENSOR_PORTS]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Sensor Configuration - Port {portName}</h3>
          <p className="text-sm text-gray-600">
            Device {selectedDevice?.deviceName || selectedDevice?.sigfoxId} • Sensor {selectedSensorNumber}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FontAwesomeIcon icon={faUndo} className="mr-2 h-4 w-4" />
              Reset
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faSave} className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Configuration */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900">Basic Configuration</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700">Sensor Name</label>
              <input
                type="text"
                value={formData.sensorName}
                onChange={(e) => handleInputChange('sensorName', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter sensor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Position/Depth (mm)
                <span className="text-gray-500 text-xs ml-1">(optional)</span>
              </label>
              <input
                type="number"
                step="1"
                value={formData.sensorPosition || ''}
                onChange={(e) =>
                  handleInputChange('sensorPosition', e.target.value ? Number(e.target.value) : undefined)
                }
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter depth in millimeters"
              />
            </div>
          </div>

          {/* Calibration */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900">Calibration</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700">Conversion Scale</label>
              <input
                type="number"
                step="0.001"
                value={formData.conversionScale}
                onChange={(e) => handleInputChange('conversionScale', Number(e.target.value))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Multiplier applied to raw sensor readings</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Conversion Offset</label>
              <input
                type="number"
                step="0.001"
                value={formData.conversionOffset}
                onChange={(e) => handleInputChange('conversionOffset', Number(e.target.value))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Value added to scaled sensor readings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Thresholds */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900">Alert Thresholds</h4>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.alertsEnabled}
              onChange={(e) => handleInputChange('alertsEnabled', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Enable Alerts</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">High Warning</label>
            <input
              type="number"
              step="0.01"
              value={formData.warnHigh || ''}
              onChange={(e) => handleInputChange('warnHigh', e.target.value ? Number(e.target.value) : undefined)}
              disabled={!formData.alertsEnabled}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-400"
              placeholder="High threshold"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Low Warning</label>
            <input
              type="number"
              step="0.01"
              value={formData.warnLow || ''}
              onChange={(e) => handleInputChange('warnLow', e.target.value ? Number(e.target.value) : undefined)}
              disabled={!formData.alertsEnabled}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-400"
              placeholder="Low threshold"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Change Warning</label>
            <input
              type="number"
              step="0.01"
              value={formData.warnChange || ''}
              onChange={(e) => handleInputChange('warnChange', e.target.value ? Number(e.target.value) : undefined)}
              disabled={!formData.alertsEnabled}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-400"
              placeholder="Change threshold"
            />
          </div>
        </div>
      </div>

      {/* Help Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Configuration Help</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong>Sensor Name:</strong> Human-readable identifier for this sensor
                </li>
                <li>
                  <strong>Position/Depth:</strong> Physical placement depth in millimeters (for soil sensors)
                </li>
                <li>
                  <strong>Conversion Scale:</strong> Calibration multiplier applied to raw readings
                </li>
                <li>
                  <strong>Conversion Offset:</strong> Calibration offset added after scaling
                </li>
                <li>
                  <strong>Thresholds:</strong> Alert triggers for high/low values or significant changes
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
